import React, { useEffect, useRef, useState } from 'react';
import { authHeaders } from '../utils/role';
import { getApiBase } from '../utils/apiConfig';

// Orchestrator Brief — the AI narrative layer, secondary to the authoritative Executive Summary (owner 2026-09-26).
// SR-364: one canonical state, two narrative layers — the brief reads the SAME page-pinned snapshot; the model
// writes prose only and cannot create, modify or override any figure, status, ranking or evidence class. When
// the narrative fails the backend readback checks it is withheld, and the Executive Summary remains the record.

interface Props { symbol: string; snapshotPin?: string | null; pageSnapshotSha?: string | null }

const OrchestratorBrief: React.FC<Props> = ({ symbol, snapshotPin, pageSnapshotSha }) => {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'withheld' | 'error'>('idle');
  const [text, setText] = useState('');
  const [meta, setMeta] = useState<{ generated_at?: string; orchestrator_version?: string; snapshot?: string }>({});
  const symRef = useRef(symbol);
  useEffect(() => { symRef.current = symbol; setState('idle'); setText(''); }, [symbol]);

  const run = () => {
    const forSym = symbol.toLowerCase();
    setState('loading');
    const q = snapshotPin ? `?snapshot=${encodeURIComponent(snapshotPin)}` : '';
    fetch(`${getApiBase()}/api/agent/orchestrate/${forSym}${q}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (symRef.current.toLowerCase() !== forSym) return;
        setMeta({ generated_at: d?.generated_at, orchestrator_version: d?.orchestrator_version, snapshot: d?.evidence_passport?.canonical_status_sha256 });
        if (d?.error || d?.narrative_readback?.status === 'FAIL' || !d?.summary) { setState('withheld'); return; }
        setText(String(d.summary));
        setState('ready');
      })
      .catch(() => setState('error'));
  };

  const strip = (s?: string | null) => (s || '').replace(/^sha256:/, '');
  const newer = !!(meta.snapshot && pageSnapshotSha && strip(meta.snapshot) !== strip(pageSnapshotSha));

  return (
    <div className="border-t border-[color:rgb(var(--c-ink-rgb)/0.08)] pt-6">
      {state === 'idle' || state === 'loading' ? (
        <button type="button" onClick={run} disabled={state === 'loading'} data-testid="orchestrator-brief-button" className="fabric-btn">
          {state === 'loading' ? 'Generating AI narrative brief…' : 'Generate AI narrative brief ✦'}
        </button>
      ) : (
        <div className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-black)] px-4 py-4 md:px-5">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text-soft)]">Orchestrator brief</span>
            <span className="font-mono text-[11px] text-[color:var(--c-text-soft)]">AI-authored narrative · canonical figures only · non-authoritative</span>
          </div>
          {state === 'ready' && <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-[color:var(--c-text-body)]">{text}</p>}
          {state === 'withheld' && <p className="text-[13px] text-[color:var(--c-text-body)]">The narrative was withheld this run — it did not clear the automated consistency checks against the canonical state. The Executive Summary above remains the record.</p>}
          {state === 'error' && <p className="text-[13px] text-[color:var(--c-text-body)]">The narrative brief is unavailable right now. The Executive Summary above remains the record.</p>}
          {newer && <p className="mt-2 text-[12px] text-[color:var(--c-amber)]">Computed on a newer snapshot than this page — read as a preview; the page remains the record.</p>}
          <div className="mt-3 space-y-0.5 font-mono text-[11px] text-[color:var(--c-text-soft)]">
            <div>The model writes prose only — it cannot create, modify or override numerical outputs, statuses or rankings.</div>
            <div>{meta.orchestrator_version || '4 deterministic tools + 1 narrative model'}{meta.generated_at ? ` · generated ${meta.generated_at}` : ''}{meta.snapshot ? ` · source snapshot ${strip(meta.snapshot).slice(0, 12)}…` : ''}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrchestratorBrief;
